// Every out-of-band id a content window points at, in one place.
//
// A `SlideContent` is a discriminated union, and four of its six kinds hold an id that
// resolves to a row in IndexedDB: `gridd` → `griddDocs`, `diagram` → `diagramDocs`,
// `gantt` → `ganttDocs`, `chart` → `chartDocs`, `image` → `assets`. Text is inline
// ProseMirror JSON and references
// nothing. Two features now need to walk those ids — `remapBundle` rewrites them when a deck
// is copied (§12.4.5), and §17.4.3's Break link rewrites them for ONE slide when it stops
// being a nugget's — and §17.4.3 asks for the remap to be extracted rather than copied,
// because a second copy is a second place to forget the kind that gets added next.
//
// The two callers differ only in how they mint the replacement id, which is the argument.

import type { SlideContent } from "@/types/content";
import type { SlideContents } from "@/types/document";

// Which store the id belongs to. `asset` is deliberately not called `image`: a diagram's
// embedded files are assets too (§21/§4.4.2), and a caller reasoning about lifetimes cares
// about the store, not about which window kind happened to mention it.
export type ContentRefKind = "grid" | "diagram" | "gantt" | "chart" | "asset";

// `(kind, id) => newId`. Returning the id unchanged is the identity remap, which is what a
// linked slide gets (§17.5.1) — so "keep this one" needs no second code path.
export type RemapRef = (kind: ContentRefKind, id: string) => string;

// One window, with every id it holds rewritten. Returns the input unchanged for `text` and
// for an empty window.
//
// `undefined` survives as `undefined`: `SlideContents` is a tuple whose second window is
// optional, and collapsing an absent window to `null` would turn a single-layout slide into
// a two-window one with an empty second pane.
export function remapContentRefs(
  content: SlideContent | undefined,
  remap: RemapRef,
): SlideContent | undefined {
  if (!content) return content;
  switch (content.kind) {
    case "gridd":
      return { ...content, griddDocId: remap("grid", content.griddDocId) };
    case "image":
      return { ...content, assetId: remap("asset", content.assetId) };
    case "diagram":
      return {
        ...content,
        diagramDocId: remap("diagram", content.diagramDocId),
      };
    case "gantt":
      return { ...content, ganttDocId: remap("gantt", content.ganttDocId) };
    case "chart":
      return { ...content, chartDocId: remap("chart", content.chartDocId) };
    default:
      return content;
  }
}

// The whole slide's windows, keeping the tuple's shape.
export function remapSlideContents(
  contents: SlideContents,
  remap: RemapRef,
): SlideContents {
  return contents.map((c) => remapContentRefs(c, remap)) as SlideContents;
}

// Read-only walk: every id one window holds, with its store. The counterpart to the remap,
// for callers that need to COPY bodies rather than rewrite references — Break link and
// Save as nugget both do exactly that before they remap anything.
export function contentRefs(
  content: SlideContent | undefined,
): { kind: ContentRefKind; id: string }[] {
  if (!content) return [];
  switch (content.kind) {
    case "gridd":
      return [{ kind: "grid", id: content.griddDocId }];
    case "image":
      return [{ kind: "asset", id: content.assetId }];
    case "diagram":
      return [{ kind: "diagram", id: content.diagramDocId }];
    case "gantt":
      return [{ kind: "gantt", id: content.ganttDocId }];
    case "chart":
      return [{ kind: "chart", id: content.chartDocId }];
    default:
      return [];
  }
}

// …and for a whole slide.
export function slideContentRefs(
  contents: SlideContents,
): { kind: ContentRefKind; id: string }[] {
  return contents.flatMap((c) => contentRefs(c));
}
